import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { omapApi } from '../../services/omapApi';

interface AgencyAction {
  id: number;
  company_id: number;
  domain: string;
  action_type: string;
  action_payload: Record<string, any>;
  rationale: string;
  estimated_impact: Record<string, any> | null;
  status: string;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
}

interface AuditEntry {
  id: number;
  event_type: string;
  domain: string;
  autonomy_level: number | null;
  details: Record<string, any>;
  created_at: string;
}

interface AgencyState {
  pendingActions: AgencyAction[];
  recentActions: AgencyAction[];
  auditLog: AuditEntry[];
  autonomyProfile: Record<string, any> | null;
  circuitBreakerState: Record<string, any> | null;
  loading: boolean;
  error: string | null;
}

const initialState: AgencyState = {
  pendingActions: [],
  recentActions: [],
  auditLog: [],
  autonomyProfile: null,
  circuitBreakerState: null,
  loading: false,
  error: null,
};

export const fetchPendingActions = createAsyncThunk(
  'agency/fetchPending',
  async (companyId: number) => {
    const data = await omapApi.getAgencyPending(companyId);
    return data.actions;
  }
);

export const fetchRecentActions = createAsyncThunk(
  'agency/fetchRecent',
  async (companyId: number) => {
    const data = await omapApi.getAgencyRecent(companyId);
    return data.actions;
  }
);

export const fetchAuditLog = createAsyncThunk(
  'agency/fetchAudit',
  async (companyId: number) => {
    const data = await omapApi.getAgencyAudit(companyId);
    return data.entries;
  }
);

export const fetchAutonomyProfile = createAsyncThunk(
  'agency/fetchProfile',
  async (companyId: number) => {
    const data = await omapApi.getAutonomyProfile(companyId);
    return data.profile;
  }
);

export const approveActionAsync = createAsyncThunk(
  'agency/approve',
  async (actionId: number) => {
    return await omapApi.approveAction(actionId);
  }
);

export const rejectActionAsync = createAsyncThunk(
  'agency/reject',
  async (actionId: number) => {
    return await omapApi.rejectAction(actionId);
  }
);

const agencySlice = createSlice({
  name: 'agency',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingActions.pending, (state) => { state.loading = true; })
      .addCase(fetchPendingActions.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingActions = action.payload;
      })
      .addCase(fetchPendingActions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pending actions';
      })
      .addCase(fetchRecentActions.fulfilled, (state, action) => {
        state.recentActions = action.payload;
      })
      .addCase(fetchAuditLog.fulfilled, (state, action) => {
        state.auditLog = action.payload;
      })
      .addCase(fetchAutonomyProfile.fulfilled, (state, action) => {
        state.autonomyProfile = action.payload;
      })
      .addCase(approveActionAsync.fulfilled, (state, action) => {
        const id = action.payload.action_id;
        state.pendingActions = state.pendingActions.filter(a => a.id !== id);
      })
      .addCase(rejectActionAsync.fulfilled, (state, action) => {
        const id = action.payload.action_id;
        state.pendingActions = state.pendingActions.filter(a => a.id !== id);
      });
  },
});

export const { clearError } = agencySlice.actions;
export default agencySlice.reducer;
